import { useCallback, useEffect, useState } from 'react';
import useAISearchCounters from '../AiSearch/api/useAISearchCounters';
import AiSearchPage from '../AiSearch/components/AiSearchPage';
import { TM_MCP_SEARCH_BY_KEYWORDS_TOOL_NAME, TM_MCP_SEARCH_SEMANTICALLY_TOOL_NAME } from './ai-ui/TMMCPConsts';
import { useCreateSession, useGetSession, useGetSessionEvents, useStartSearch } from './api/useAgentChatClient';
import AgentChatWindow from './components/AgentChatWindow';
import { AnthropicManagedAgentSessionContextProvider } from './store/AnthropicManagedAgentSessionContext';
import { AgentStreamEvent } from './types';
import { LoadingOverlay } from '@/Components/shared';
import useRecruitmentId from '@/Hooks/useRecruitmentId';

const SEARCH_TOOL_NAMES = new Set([TM_MCP_SEARCH_BY_KEYWORDS_TOOL_NAME, TM_MCP_SEARCH_SEMANTICALLY_TOOL_NAME]);

const AgenticSearchPage = () => {
    const recruitmentId = useRecruitmentId();
    const { mutate: mutateAISearchCounters } = useAISearchCounters();
    const { trigger: getSessionTrigger } = useGetSession(recruitmentId);
    const { trigger: createSessionTrigger } = useCreateSession(recruitmentId);
    const { trigger: getSessionEventsTrigger } = useGetSessionEvents(recruitmentId);
    const { trigger: startSearchTrigger } = useStartSearch(recruitmentId);

    const [sessionId, setSessionId] = useState<string | null>(null);
    const [initialEvents, setInitialEvents] = useState<AgentStreamEvent[]>([]);
    const [isLoading, setIsLoading] = useState(true);

    // when keyword search or semantic search results come in,
    // refresh the counters in the parent page to reflect the new search results
    const onMcpToolResult = useCallback(
        (toolName: string) => {
            if (SEARCH_TOOL_NAMES.has(toolName)) {
                mutateAISearchCounters();
            }
        },
        [mutateAISearchCounters],
    );

    useEffect(() => {
        async function initSession() {
            const existing = await getSessionTrigger();
            if (existing) {
                const sessionEvents = await getSessionEventsTrigger(existing.sessionId);
                setInitialEvents(sessionEvents);
                setSessionId(existing.sessionId);
            } else {
                const session = await createSessionTrigger();
                await startSearchTrigger(session.sessionId);
                setSessionId(session.sessionId);
            }
            setIsLoading(false);
        }
        initSession();
    }, [recruitmentId]);

    return (
        <AiSearchPage>
            {isLoading || sessionId === null ? (
                <LoadingOverlay className="static flex-1" />
            ) : (
                <AnthropicManagedAgentSessionContextProvider
                    sessionId={sessionId}
                    initialEvents={initialEvents}
                    onMcpToolResult={onMcpToolResult}
                >
                    <AgentChatWindow />
                </AnthropicManagedAgentSessionContextProvider>
            )}
        </AiSearchPage>
    );
};

export default AgenticSearchPage;
